import React, { useState } from "react";
import { Plus, Truck, Building2, Clock, CheckCircle2, AlertTriangle, Eye, ArrowRight } from "lucide-react";
import { useApp } from "../../app/context/AppContext";
import { DataTable } from "../../components/common/DataTable";
import { Badge } from "../../components/common/Badge";
import { formatCurrency, formatDate } from "../../utils/formatters";
import { PurchaseOrderModal } from "./PurchaseOrderModal";
import { useNavigate } from "react-router-dom";

export function ProcurementModule() {
  const { purchaseOrders, suppliers, updatePOStatus } = useApp();
  const [activeTab, setActiveTab] = useState("orders"); // 'orders' | 'suppliers'
  const [isPOModalOpen, setIsPOModalOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const navigate = useNavigate();

  // PO Table Columns
  const poColumns = [
    {
      header: "PO Number",
      accessorKey: "poNumber",
      render: (val, row) => (
        <div>
          <span className="font-bold text-slate-900 dark:text-white font-mono">{val}</span>
          <p className="text-[10px] text-slate-400">{formatDate(row.orderDate)}</p>
        </div>
      )
    },
    {
      header: "Supplier",
      accessorKey: "supplierName",
      render: (val) => <span className="font-medium text-slate-800 dark:text-slate-200">{val}</span>
    },
    {
      header: "Destination WH",
      accessorKey: "warehouseName",
      render: (val) => <span className="text-slate-600 dark:text-slate-400 text-[11px]">{val?.split("(")[0]}</span>
    },
    {
      header: "Items Count",
      accessorKey: "items",
      render: (items) => `${items?.length || 0} line items`
    },
    {
      header: "Total (₹)",
      accessorKey: "totalAmount",
      align: "right",
      render: (val, row) => (
        <div>
          <span className="font-bold text-slate-900 dark:text-white font-mono">{formatCurrency(val)}</span>
          <p className="text-[10px] text-slate-400">Landed: {formatCurrency(row.landedCost)}</p>
        </div>
      )
    },
    {
      header: "Status Pipeline",
      accessorKey: "status",
      render: (status) => {
        const map = {
          Draft: { variant: "default", text: "Draft" },
          Sent: { variant: "blue", text: "Sent to Vendor" },
          "Partially Received": { variant: "amber", text: "Partially Received" },
          Received: { variant: "emerald", text: "Received" },
          Closed: { variant: "purple", text: "Closed / Archived" }
        };
        const st = map[status] || map.Draft;
        return <Badge variant={st.variant}>{st.text}</Badge>;
      }
    },
    {
      header: "Actions",
      accessorKey: "actions",
      sortable: false,
      render: (_, row) => (
        <div className="flex items-center gap-1.5 justify-end">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedPO(row);
            }}
            className="p-1.5 rounded-lg text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800"
            title="View PO Details"
          >
            <Eye className="w-3.5 h-3.5" />
          </button>
          {row.status === "Draft" && (
            <button
              onClick={async (e) => {
                e.stopPropagation();
                await updatePOStatus(row.id, "Sent");
              }}
              className="px-2 py-0.5 rounded bg-sky-50 dark:bg-sky-950 text-sky-600 dark:text-sky-400 text-[10px] font-semibold hover:bg-sky-100"
            >
              Mark Sent
            </button>
          )}
          {(row.status === "Sent" || row.status === "Partially Received") && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/receiving?po=${row.id}`);
              }}
              className="px-2 py-0.5 rounded bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 text-[10px] font-semibold hover:bg-emerald-100"
            >
              Receive GRN →
            </button>
          )}
        </div>
      )
    }
  ];

  // Suppliers Table Columns
  const supplierColumns = [
    {
      header: "Supplier Name",
      accessorKey: "name",
      render: (val, row) => (
        <div>
          <span className="font-bold text-slate-900 dark:text-white">{val}</span>
          <p className="text-[10px] text-slate-400">{row.city}, {row.state}</p>
        </div>
      )
    },
    {
      header: "Contact Person",
      accessorKey: "contactPerson",
      render: (val, row) => (
        <div>
          <p className="font-medium text-slate-700 dark:text-slate-300">{val}</p>
          <p className="text-[10px] text-slate-400 font-mono">{row.phone}</p>
        </div>
      )
    },
    {
      header: "Payment Terms",
      accessorKey: "paymentTerms",
      render: (val) => <span className="font-mono text-slate-600 dark:text-slate-400">{val}</span>
    },
    {
      header: "Rating",
      accessorKey: "performanceRating",
      render: (val) => (
        <span className="inline-flex items-center gap-1 font-bold text-emerald-600 dark:text-emerald-400">
          ★ {val} / 5.0
        </span>
      )
    },
    {
      header: "Outstanding Balance",
      accessorKey: "outstandingBalance",
      align: "right",
      render: (val) => (
        <span className="font-bold font-mono text-rose-600 dark:text-rose-400">
          {formatCurrency(val)}
        </span>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Top Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display flex items-center gap-2">
            <Truck className="w-5 h-5 text-emerald-500" />
            Procurement & Purchase Orders
          </h2>
          <p className="text-xs text-slate-500">
            Multi-line PO generation, landed cost calculators, supplier terms, and delivery pipelines
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <div className="flex p-1 bg-slate-100 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 text-xs">
            <button
              onClick={() => setActiveTab("orders")}
              className={`px-3 py-1 rounded-lg font-semibold transition ${
                activeTab === "orders" ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-xs" : "text-slate-500 hover:text-slate-800"
              }`}
            >
              Purchase Orders ({purchaseOrders.length})
            </button>
            <button
              onClick={() => setActiveTab("suppliers")}
              className={`px-3 py-1 rounded-lg font-semibold transition ${
                activeTab === "suppliers" ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-xs" : "text-slate-500 hover:text-slate-800"
              }`}
            >
              Suppliers Directory ({suppliers.length})
            </button>
          </div>

          <button
            onClick={() => setIsPOModalOpen(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-xl shadow-lg shadow-emerald-600/30 transition"
          >
            <Plus className="w-4 h-4" />
            Create Purchase Order
          </button>
        </div>
      </div>

      {/* Main Data View */}
      {activeTab === "orders" ? (
        <DataTable
          columns={poColumns}
          data={purchaseOrders}
          title="Purchase Orders Register"
          subtitle="Click on any record to inspect discrepancies, landed costs, and line-item receipts"
          filename="kirana_purchase_orders"
          onRowClick={(row) => setSelectedPO(row)}
        />
      ) : (
        <DataTable
          columns={supplierColumns}
          data={suppliers}
          title="Approved FMCG & Agro Suppliers"
          subtitle="Vendor credit limits, performance scores, and payment histories"
          filename="kirana_suppliers"
        />
      )}

      {/* PO Detail Drawer */}
      {selectedPO && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/60 backdrop-blur-sm flex justify-end animate-in fade-in duration-200">
          <div className="w-full max-w-xl bg-white dark:bg-slate-900 h-full p-6 shadow-2xl flex flex-col justify-between overflow-y-auto border-l border-slate-200 dark:border-slate-800">
            <div className="space-y-4">
              <div className="flex items-start justify-between border-b border-slate-100 dark:border-slate-800 pb-4">
                <div>
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                    Purchase Order Detail
                  </span>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white font-mono">
                    {selectedPO.poNumber}
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">{selectedPO.supplierName}</p>
                </div>
                <button
                  onClick={() => setSelectedPO(null)}
                  className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  ✕
                </button>
              </div>

              {/* Status & Timing */}
              <div className="grid grid-cols-2 gap-3 bg-slate-50 dark:bg-slate-950 p-3 rounded-xl border border-slate-200/60 dark:border-slate-800 text-xs">
                <div>
                  <p className="text-slate-400">Order Date:</p>
                  <p className="font-semibold text-slate-800 dark:text-slate-200">{formatDate(selectedPO.orderDate)}</p>
                </div>
                <div>
                  <p className="text-slate-400">Expected Delivery:</p>
                  <p className="font-semibold text-slate-800 dark:text-slate-200">{formatDate(selectedPO.expectedDate)}</p>
                </div>
                <div>
                  <p className="text-slate-400">Status:</p>
                  <p className="font-semibold text-emerald-600 dark:text-emerald-400">{selectedPO.status}</p>
                </div>
                <div>
                  <p className="text-slate-400">Destination:</p>
                  <p className="font-semibold text-slate-800 dark:text-slate-200">{selectedPO.warehouseName}</p>
                </div>
              </div>

              {/* Line Items with Ordered vs Received Discrepancy Tracking */}
              <div>
                <h4 className="text-xs font-bold text-slate-900 dark:text-white mb-2">
                  Line Items & Shortage Discrepancy Matrix
                </h4>
                <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden text-xs">
                  <table className="w-full text-left">
                    <thead className="bg-slate-50 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 text-slate-500 uppercase font-semibold text-[10px]">
                      <tr>
                        <th className="p-2.5">Product</th>
                        <th className="p-2.5 text-center">Ordered</th>
                        <th className="p-2.5 text-center">Received</th>
                        <th className="p-2.5 text-right">Unit Cost</th>
                        <th className="p-2.5 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {selectedPO.items?.map((item, iIdx) => {
                        const isShortage = (item.receivedQty || 0) < item.orderedQty && selectedPO.status !== "Draft";
                        return (
                          <tr key={iIdx}>
                            <td className="p-2.5 font-medium text-slate-800 dark:text-slate-200">
                              {item.productName}
                            </td>
                            <td className="p-2.5 text-center font-mono">{item.orderedQty}</td>
                            <td className="p-2.5 text-center font-mono">
                              <span className={isShortage ? "text-rose-500 font-bold" : "text-emerald-600"}>
                                {item.receivedQty || 0}
                              </span>
                            </td>
                            <td className="p-2.5 text-right font-mono text-slate-500">
                              ₹{item.unitPrice}
                            </td>
                            <td className="p-2.5 text-right font-mono font-semibold text-slate-900 dark:text-white">
                              {formatCurrency(item.total || item.orderedQty * item.unitPrice)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Landed Cost Breakdown */}
              <div className="bg-slate-50 dark:bg-slate-950 p-3 rounded-xl border border-slate-200/60 dark:border-slate-800 text-xs space-y-1.5">
                <div className="flex justify-between text-slate-500">
                  <span>Product Items Subtotal:</span>
                  <span className="font-mono">{formatCurrency(selectedPO.totalAmount)}</span>
                </div>
                <div className="flex justify-between text-slate-500">
                  <span>Freight & Transportation:</span>
                  <span className="font-mono">{formatCurrency(selectedPO.freightCharges || 0)}</span>
                </div>
                <div className="flex justify-between text-slate-500">
                  <span>Packaging & Handling:</span>
                  <span className="font-mono">{formatCurrency((selectedPO.packagingCharges || 0) + (selectedPO.handlingCharges || 0))}</span>
                </div>
                <div className="flex justify-between font-bold text-slate-900 dark:text-white pt-2 border-t border-slate-200 dark:border-slate-800">
                  <span>Total Landed Cost:</span>
                  <span className="font-mono text-emerald-600 dark:text-emerald-400">
                    {formatCurrency(selectedPO.landedCost || selectedPO.totalAmount)}
                  </span>
                </div>
              </div>

              {selectedPO.notes && (
                <div className="text-xs bg-slate-50 dark:bg-slate-950 p-3 rounded-xl border border-slate-200/60 dark:border-slate-800 text-slate-600 dark:text-slate-400">
                  <p className="font-semibold text-slate-700 dark:text-slate-300">Instructions / Notes:</p>
                  <p className="mt-0.5">{selectedPO.notes}</p>
                </div>
              )}
            </div>

            <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-end gap-2 text-xs">
              <button
                onClick={() => setSelectedPO(null)}
                className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl"
              >
                Close
              </button>
              {(selectedPO.status === "Sent" || selectedPO.status === "Partially Received") && (
                <button
                  onClick={() => {
                    const id = selectedPO.id;
                    setSelectedPO(null);
                    navigate(`/receiving?po=${id}`);
                  }}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-xl shadow"
                >
                  Receive GRN Putaway →
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* PO Creation Modal */}
      <PurchaseOrderModal
        isOpen={isPOModalOpen}
        onClose={() => setIsPOModalOpen(false)}
      />
    </div>
  );
}
